/**
 * AssetPreview.jsx
 * ----------------
 * Preview component for different asset types (images, PDFs, videos, audio)
 */

import React from 'react';
import { FileText, Music, File, Download } from 'lucide-react';

const AssetPreview = ({ asset }) => {
  if (!asset) return null;

  const renderPreview = () => {
    // Image preview
    if (asset.fileType.startsWith('image/')) {
      return (
        <img 
          src={asset.downloadURL} 
          alt={asset.name}
          style={{
            maxWidth: '100%',
            maxHeight: '70vh',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }

    // Video preview
    if (asset.fileType.startsWith('video/')) {
      return (
        <video 
          controls
          style={{
            maxWidth: '100%',
            maxHeight: '70vh',
            display: 'block',
            margin: '0 auto'
          }}
        >
          <source src={asset.downloadURL} type={asset.fileType} />
          Your browser does not support video playback.
        </video>
      );
    }

    // Audio preview
    if (asset.fileType.startsWith('audio/')) {
      return (
        <div style={{ 
          padding: '40px',
          textAlign: 'center'
        }}>
          <Music className="w-24 h-24" style={{ 
            margin: '0 auto 24px',
            color: '#9CA3AF'
          }} />
          <audio 
            controls
            style={{
              width: '100%',
              maxWidth: '500px'
            }}
          >
            <source src={asset.downloadURL} type={asset.fileType} />
            Your browser does not support audio playback.
          </audio>
        </div>
      );
    }

    // PDF preview
    if (asset.fileType === 'application/pdf') {
      return (
        <div style={{ height: '70vh', width: '100%' }}>
          <iframe
            src={asset.downloadURL}
            title={asset.name}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
          />
        </div>
      );
    }

    // Document/other file types
    return (
      <div style={{ 
        padding: '60px 40px',
        textAlign: 'center'
      }}>
        {asset.fileType.includes('pdf') ? (
          <FileText className="w-24 h-24" style={{ 
            margin: '0 auto 24px',
            color: '#9CA3AF'
          }} />
        ) : (
          <File className="w-24 h-24" style={{ 
            margin: '0 auto 24px',
            color: '#9CA3AF'
          }} />
        )}
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#374151',
          marginBottom: '8px'
        }}>
          {asset.name}
        </h3>
        <p style={{
          fontSize: '14px',
          color: '#6B7280',
          marginBottom: '24px'
        }}>
          Preview not available for this file type
        </p>
        <a
          href={asset.downloadURL}
          download={asset.name}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            backgroundColor: '#3B82F6',
            color: 'white',
            borderRadius: '8px',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          <Download className="w-5 h-5" />
          Download File
        </a>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      {renderPreview()}
      
      {/* Asset Info Footer */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#F9FAFB',
        borderRadius: '8px',
        borderTop: '1px solid #E5E7EB'
      }}>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <div>
            <div style={{ 
              fontSize: '12px',
              color: '#6B7280',
              marginBottom: '4px'
            }}>
              File Name
            </div>
            <div style={{ 
              fontSize: '14px',
              fontWeight: '600',
              color: '#111827'
            }}>
              {asset.name}
            </div>
          </div>
          
          <div>
            <div style={{ 
              fontSize: '12px',
              color: '#6B7280',
              marginBottom: '4px'
            }}>
              File Type
            </div>
            <div style={{ 
              fontSize: '14px',
              fontWeight: '600',
              color: '#111827'
            }}>
              {asset.fileType}
            </div>
          </div>
          
          <div>
            <div style={{ 
              fontSize: '12px',
              color: '#6B7280',
              marginBottom: '4px'
            }}>
              Size
            </div>
            <div style={{ 
              fontSize: '14px',
              fontWeight: '600',
              color: '#111827'
            }}>
              {(asset.size / 1024).toFixed(1)} KB
            </div>
          </div>
          
          {asset.uploadedAt && (
            <div>
              <div style={{ 
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px'
              }}>
                Uploaded
              </div>
              <div style={{ 
                fontSize: '14px',
                fontWeight: '600',
                color: '#111827'
              }}>
                {new Date(asset.uploadedAt).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
        
        {asset.description && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ 
              fontSize: '12px',
              color: '#6B7280',
              marginBottom: '4px'
            }}>
              Description
            </div>
            <div style={{ 
              fontSize: '14px',
              color: '#374151'
            }}>
              {asset.description}
            </div>
          </div>
        )}
        
        <div style={{ marginTop: '16px' }}>
          <a
            href={asset.downloadURL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#3B82F6',
              color: 'white',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '13px',
              fontWeight: '600'
            }}
          >
            <Download className="w-4 h-4" />
            Download
          </a>
        </div>
      </div>
    </div>
  );
};

export default AssetPreview;









